import React, { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { RefreshCw, Globe } from "lucide-react";
import { useConfirm } from "@/lib/confirm";

// Preview-only helper: mirror the live site's CMS content into this environment (one click, shared key).
export const ContentSyncCard = () => {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [fallback, setFallback] = useState("");
  const confirm = useConfirm();
  const isLive = /2rightfeetdanceschool\.com\.au$/i.test(window.location.hostname);
  const loadStatus = () => api.get("/admin/content-sync-status").then(r => { setStatus(r.data); setFallback(r.data.fallback_url || ""); }).catch(() => {});
  const saveFallback = async () => { try { await api.put("/admin/content-sync-fallback", { url: fallback }); toast.success("Fallback URL saved"); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } };
  useEffect(() => { if (!isLive) loadStatus(); }, [isLive]);
  if (isLive) return null;

  const ago = (iso) => { const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000)); return m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`; };
  const stale = status?.last_synced_at && Date.now() - new Date(status.last_synced_at) > 26 * 3600 * 1000;

  const run = async () => {
    if (!(await confirm({ title: "Replace this preview's content with the live site's?", description: "Settings, classes, terms, gallery, blog, promo codes, events, workshops and media records here will be overwritten. Admin users, registrations and payments are untouched.", danger: true, confirmLabel: "Pull from live" }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/content-sync-from-live", {});
      toast.success(`Preview now mirrors the live site (${Object.values(data.counts).reduce((a, b) => a + b, 0)} records)`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); loadStatus(); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-surface p-6 border border-amber-500/30" data-testid="content-sync-card">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2"><Globe size={16} className="text-amber-400"/><h3 className="font-display text-2xl uppercase">Pull content from live site</h3></div>
        {status && (
          <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${status.last_synced_at ? (stale ? "border-amber-500/40 text-amber-400" : "border-emerald-500/40 text-emerald-400") : "border-white/15 text-zinc-500"}`} data-testid="sync-status-badge">
            {status.last_synced_at ? `Last synced from live: ${ago(status.last_synced_at)} · ${status.mode}` : "Never synced"}
          </span>
        )}
      </div>
      <p className="text-zinc-400 text-sm mb-3">You're on the <strong>preview</strong>. One click copies everything from <span className="text-zinc-200">{status?.live_url || "the live site"}</span> here. Also runs automatically every night at midnight AEST.</p>
      {status?.last_error && (!status.last_synced_at || new Date(status.last_error_at) > new Date(status.last_synced_at)) && (
        <p className="text-amber-400 text-xs mb-3" data-testid="sync-last-error">Last automatic sync failed: {status.last_error}</p>
      )}
      {status && (
        <div className="flex flex-wrap items-end gap-2 mb-4" data-testid="sync-fallback-row">
          <div className="flex-1 min-w-[260px]">
            <label className="eyebrow block mb-1">Fallback live URL (used if the domain above can't be reached)</label>
            <input value={fallback} onChange={e => setFallback(e.target.value)} placeholder="https://your-app.emergent.host" className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm" data-testid="sync-fallback-input" />
          </div>
          <button type="button" onClick={saveFallback} className="text-[11px] uppercase tracking-widest border border-white/20 px-3 py-2.5 hover:border-white/50" data-testid="sync-fallback-save">Save</button>
        </div>
      )}
      {status && !status.enabled_here && <p className="text-zinc-500 text-xs mb-3">Inbound sync is disabled here (CONTENT_SYNC_ROLE is not "preview").</p>}
      <button onClick={run} disabled={busy || (status && !status.enabled_here)} className="btn-red flex items-center gap-2 disabled:opacity-50" data-testid="sync-run">
        <RefreshCw size={14} className={busy ? "animate-spin" : ""}/>{busy ? "Pulling…" : "Pull from live"}
      </button>
    </div>
  );
};
